// TITLE/TEXT blocks: plain string (TITLE) or rich HTML string (TEXT) per language.
export type TextContent = Record<string, string>;
// IMAGE blocks: one separately-uploaded image variant per language.
export type ImageContent = Record<string, { path: string; url: string }>;

export class ContentBlock {
  constructor(
    public readonly id: string,
    public readonly type: string,
    public readonly content: TextContent | ImageContent,
  ) {}

  static fromJson(json: any): ContentBlock {
    return new ContentBlock(json["id"], json["type"], json["content"] ?? {});
  }

  toJson(): object {
    return {
      id: this.id,
      type: this.type,
      content: this.content,
    };
  }
}

export class CardAction {
  constructor(
    public readonly id: string,
    public readonly answerValue: string,
    public readonly actionType: string,
    public readonly nextCardUuid: string | null,
  ) {}

  static fromJson(json: any): CardAction {
    return new CardAction(
      json["id"],
      json["answer_value"],
      json["action_type"],
      json["next_card_uuid"] ?? null,
    );
  }

  toJson(): object {
    return {
      id: this.id,
      answer_value: this.answerValue,
      action_type: this.actionType,
      next_card_uuid: this.nextCardUuid,
    };
  }
}

export class FlowCard {
  constructor(
    public readonly uuid: string,
    // The card's own name/title (the card's heading) — distinct from a "title"-type content block,
    // which is a sub-heading within the card's body content. Per-language, same shape as content
    // block text.
    public readonly name: TextContent,
    public readonly cardType: string,
    public readonly contentBlocks: ContentBlock[],
    public readonly actions: CardAction[],
    public readonly column: number,
    public readonly positionInColumn: number,
    // The flow's starting card — at most one per flow; marking one as first unmarks the previous one
    // server-side.
    public readonly isFirst: boolean,
  ) {}

  static fromJson(json: any): FlowCard {
    return new FlowCard(
      json["uuid"],
      json["name"] ?? {},
      json["card_type"],
      (json["content_blocks"] ?? []).map((block: any) => ContentBlock.fromJson(block)),
      (json["actions"] ?? []).map((action: any) => CardAction.fromJson(action)),
      json["column"] ?? 0,
      json["position_in_column"] ?? 0,
      json["is_first"] ?? false,
    );
  }

  // The full-replace PUT body. Static rather than an instance method, because the editor updates
  // cards as spread copies (`{...card, isFirst}`), which lose the class prototype.
  static toJson(card: FlowCard): object {
    return {
      name: card.name,
      card_type: card.cardType,
      content_blocks: card.contentBlocks.map((block) => block.toJson()),
      actions: card.actions.map((action) => action.toJson()),
      column: card.column,
      position_in_column: card.positionInColumn,
      is_first: card.isFirst,
    };
  }
}
